// Consent state shared by the browser, the proxy, server components and the
// Stripe webhook. Our own banner (components/consent/CookieBanner) writes the
// `gg_consent` cookie, e.g. "v1.a0.m1.t1761660000": version, analytics,
// marketing (advertisement) and the time of the choice in seconds.
//
// Visitors who chose in CookieYes (the banner before ours) keep that choice
// until CookieYes' own cookie expires: `cookieyes-consent`, e.g.
// "consentid:abc,consent:no,action:yes,necessary:yes,functional:no,
// analytics:no,performance:no,advertisement:no,other:no". It is read, never
// copied, so honouring it does not extend its lifetime. Ours wins when both
// exist.
//
// Everything defaults to "no": a missing, empty or malformed cookie means the
// visitor has not consented (Danish/EU prior-consent rules).

export const CONSENT_COOKIE = "gg_consent";
/** Bump to ask every visitor again (e.g. after a banner fault). */
export const CONSENT_COOKIE_VERSION = 1;
/** A choice is asked again after 12 months. */
export const CONSENT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
export const COOKIEYES_CONSENT_COOKIE = "cookieyes-consent";

export interface TrackingConsent {
  /**
   * Advertising: ad pixels, ad cookies and server-side ad events. Ad pixels
   * and events also need production (see environment.ts).
   */
  advertisement: boolean;
  /** Analytics: attribution cookies and persistent PostHog storage. */
  analytics: boolean;
}

export const NO_TRACKING_CONSENT: TrackingConsent = Object.freeze({
  advertisement: false,
  analytics: false,
});

export type ConsentSource = "guestgallery" | "cookieyes";

/** A visitor's choice, or null while they have not chosen. */
export interface ConsentDecision {
  consent: TrackingConsent;
  source: ConsentSource;
}

interface CookieReader {
  get(name: string): { value: string } | undefined;
}

const CONSENT_COOKIE_PATTERN = /^v(\d{1,3})\.a([01])\.m([01])\.t(\d{9,11})$/;
// A choice dated more than this far ahead is not one our banner wrote.
const CLOCK_SKEW_SECONDS = 24 * 60 * 60;

export function serializeConsentCookie(
  consent: TrackingConsent,
  recordedAtSeconds: number,
): string {
  return `v${CONSENT_COOKIE_VERSION}.a${consent.analytics ? 1 : 0}.m${
    consent.advertisement ? 1 : 0
  }.t${Math.floor(recordedAtSeconds)}`;
}

/**
 * Our cookie: null when missing, malformed, from another version, older than
 * 12 months or dated in the future, so the banner asks.
 */
export function parseConsentCookie(
  value: string | null | undefined,
  nowSeconds: number,
): TrackingConsent | null {
  const match = value ? CONSENT_COOKIE_PATTERN.exec(value.trim()) : null;
  if (!match || Number(match[1]) !== CONSENT_COOKIE_VERSION) return null;
  const recordedAt = Number(match[4]);
  if (
    recordedAt > nowSeconds + CLOCK_SKEW_SECONDS ||
    nowSeconds - recordedAt > CONSENT_MAX_AGE_SECONDS
  ) {
    return null;
  }
  return { analytics: match[2] === "1", advertisement: match[3] === "1" };
}

/**
 * A CookieYes choice, mapped per category: "analytics" to analytics,
 * "advertisement" to advertising. Null (ask again) unless the visitor made a
 * choice (`action:yes`) and both categories say yes or no.
 */
export function parseCookieYesDecision(
  value: string | null | undefined,
): TrackingConsent | null {
  if (!value) return null;

  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // Keep the raw value; CookieYes normally writes it unencoded.
  }

  const categories = new Map<string, string>();
  for (const part of decoded.split(",")) {
    const separator = part.indexOf(":");
    if (separator <= 0) continue;
    const key = part.slice(0, separator).trim().toLowerCase();
    // First occurrence wins so an appended duplicate cannot flip a category.
    if (!categories.has(key)) {
      categories.set(key, part.slice(separator + 1).trim().toLowerCase());
    }
  }

  const isFlag = (flag: string | undefined): boolean => flag === "yes" || flag === "no";
  const analytics = categories.get("analytics");
  const advertisement = categories.get("advertisement");
  if (categories.get("action") !== "yes" || !isFlag(analytics) || !isFlag(advertisement)) {
    return null;
  }
  return { analytics: analytics === "yes", advertisement: advertisement === "yes" };
}

export function parseCookieYesConsent(
  value: string | null | undefined,
): TrackingConsent {
  return parseCookieYesDecision(value) ?? NO_TRACKING_CONSENT;
}

/** Our cookie first, then a CookieYes choice; null while undecided. */
export function readConsentDecision(
  cookieStore: CookieReader,
  nowSeconds: number = Date.now() / 1000,
): ConsentDecision | null {
  const ours = parseConsentCookie(cookieStore.get(CONSENT_COOKIE)?.value, nowSeconds);
  if (ours) return { consent: ours, source: "guestgallery" };
  const cookieYes = parseCookieYesDecision(
    cookieStore.get(COOKIEYES_CONSENT_COOKIE)?.value,
  );
  return cookieYes ? { consent: cookieYes, source: "cookieyes" } : null;
}

export function readTrackingConsent(cookieStore: CookieReader): TrackingConsent {
  return readConsentDecision(cookieStore)?.consent ?? NO_TRACKING_CONSENT;
}

export type ConsentFlag = "yes" | "no";

function toConsentFlag(value: boolean): ConsentFlag {
  return value ? "yes" : "no";
}

/** Consent at checkout creation, stored on the Stripe session. */
export function getConsentStripeMetadata(
  consent: TrackingConsent,
): { consent_advertisement: ConsentFlag; consent_analytics: ConsentFlag } {
  return {
    consent_advertisement: toConsentFlag(consent.advertisement),
    consent_analytics: toConsentFlag(consent.analytics),
  };
}

/**
 * Consent recorded on a Stripe checkout session. Sessions created before the
 * consent fields existed have no value and count as "no".
 */
export function getConsentFromStripeMetadata(
  metadata: Record<string, string> | null | undefined,
): TrackingConsent {
  return {
    advertisement: metadata?.consent_advertisement === "yes",
    analytics: metadata?.consent_analytics === "yes",
  };
}
